/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 07/08/2025 - 17:23:38
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 07/08/2025
    * - Author          : 
    * - Modification    : 
**/
import React, { useEffect, useState } from "react";
import MapPageHeader from "./components/MapPageHeader";
import MapPageRender from "./components/MapPageRender";
import MapPageSidebar from "./components/MapPageSidebar";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { resetAllProjectData } from '../../store/reducers/bmapSlice';
import { useDispatch } from 'react-redux';

const MapPage: React.FC = () => {
  // Use useParams to get projectId from URL
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(['mapPage', 'common']);
  const [polygonNames, setPolygonNames] = useState<string[]>([]);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [validatedProjectId, setValidatedProjectId] = useState<string | null>(null);

  // Function to update the name of a polygon
  const updatePolygonName = (index: number, newName: string) => {
    setPolygonNames((prevNames) => {
      const updatedNames = [...prevNames];
      updatedNames[index] = newName;
      return updatedNames;
    });
  };

  const handleNameChange = (index: number, newName: string) => {
    updatePolygonName(index, newName);
  };

  // Reset Redux state when unmounting
  useEffect(() => {
    return () => {
      dispatch(resetAllProjectData());
    };
  }, [dispatch]);

  // Validate projectId and set up initial state
  useEffect(() => {
    console.log("MapPage mounting with projectId:", projectId);
    
    // Add a small delay to ensure params are fully loaded
    const timer = setTimeout(() => {
      if (!projectId) {
        console.error("No projectId found in URL params, redirecting to start page");
        navigate("/start");
        return;
      }

      // Validate and store the projectId
      const validId = String(projectId).trim();
      if (validId === "") {
        console.error("Empty projectId, redirecting to start page");
        navigate("/start");
        return;
      }

      console.log("Setting validated project ID:", validId);
      setValidatedProjectId(validId);
      setIsLoading(false);
    }, 100); // Small delay to ensure params are loaded

    // Check screen width for mobile detection
    const checkScreenWidth = () => {
      setIsMobileOrTablet(window.innerWidth < 768);
    };

    checkScreenWidth();
    window.addEventListener('resize', checkScreenWidth);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScreenWidth);
    };
  }, [projectId, navigate]);

  // Show mobile message if on small screen
  if (isMobileOrTablet) {
    const mobileMessage = i18n.exists('mapPage:errors.mobileMessage') 
      ? t('mapPage:errors.mobileMessage') 
      : "Map is only available on desktop screens";
      
    const mobileMessageDescription = i18n.exists('mapPage:errors.mobileMessageDescription')
      ? t('mapPage:errors.mobileMessageDescription')
      : "Please use a desktop or laptop to access the map.";
      
    return (
      <div className="mobile-message">
        <h1>{mobileMessage}</h1>
        <p>{mobileMessageDescription}</p>
      </div>
    );
  }

  // Show loading state
  if (isLoading || !validatedProjectId) {
    return (
      <div className="loading-container" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>
          <div className="spinner" style={{ 
            border: '4px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '50%',
            borderTop: '4px solid #3498db',
            width: '40px',
            height: '40px',
            margin: '0 auto 20px auto',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p>Loading project data...</p>
        </div>
        <style>
          {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          `}
        </style>
      </div>
    );
  }

  // Render main content once we have a valid projectId
  return (
    <div>
      <MapPageHeader />
      <div className="Map-page-content">
        <div className="Map-render">
          {/* Now we can be sure validatedProjectId is a valid string */}
          <MapPageRender key={validatedProjectId} />
        </div>
        <div className="Map-sidebar">
          <MapPageSidebar />
        </div>
      </div>
    </div>
  );
};

export default MapPage;